import { useCallback, useRef, useState } from 'react';

interface AudioFile {
  filename: string;
  duration?: number; // in milliseconds
}

export const usePreKAudio = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef(false);
  const activeAudioElements = useRef<Set<HTMLAudioElement>>(new Set());
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  const enableAudio = useCallback(() => {
    setUserHasInteracted(true);
  }, []);

  // Enhanced detection: Check multiple sources for any playing audio
  const isAnyAudioPlaying = useCallback(() => {
    // Layer 1: Check our playing flag
    if (isPlayingRef.current) {
      return true;
    }

    // Layer 2: Check our tracked audio element
    if (audioRef.current && !audioRef.current.paused) {
      return true;
    }

    // Layer 3: Check all our created audio elements
    const activeAudioArray = Array.from(activeAudioElements.current);
    for (const audio of activeAudioArray) {
      if (!audio.paused) {
        return true;
      }
    }

    // Layer 4: Global DOM audio detection (nuclear option)
    const allAudio = Array.from(document.querySelectorAll('audio'));
    for (const audio of allAudio) {
      if (!audio.paused) {
        return true;
      }
    }

    return false;
  }, []);

  // Force stop all audio - aggressive cleanup
  const forceStopAllAudio = useCallback(() => {
    // Stop our tracked audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // Stop all our created audio elements
    const activeAudioArray = Array.from(activeAudioElements.current);
    for (const audio of activeAudioArray) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (e) {
        // Silently handle errors
      }
    }

    // Nuclear option: stop ALL audio in DOM
    const allAudio = Array.from(document.querySelectorAll('audio'));
    for (const audio of allAudio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (e) {
        // Silently handle errors
      }
    }

    // Reset all flags
    isPlayingRef.current = false;
    activeAudioElements.current.clear();
  }, []);

  const playAudio = useCallback(async (audioFile: AudioFile): Promise<void> => {
    // ENHANCED RULE: If ANY audio is detected playing, don't start new audio
    if (isAnyAudioPlaying()) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        // Create new audio element
        const audio = new Audio(`/assets/audio/${audioFile.filename}`);
        audioRef.current = audio;
        activeAudioElements.current.add(audio);
        isPlayingRef.current = true;

        // Set up event handlers
        audio.onended = () => {
          isPlayingRef.current = false;
          activeAudioElements.current.delete(audio);
          resolve();
        };

        audio.onerror = (error) => {
          isPlayingRef.current = false;
          activeAudioElements.current.delete(audio);
          reject(new Error(`Audio file not found: ${audioFile.filename}`));
        };

        audio.oncanplaythrough = () => {
          // Try to play immediately
          audio.play()
            .then(() => {
              // Audio playing successfully
            })
            .catch((error) => {
              // Handle autoplay restrictions
              isPlayingRef.current = false;
              if (error.name === 'NotAllowedError') {
                activeAudioElements.current.delete(audio);
                resolve(); // Don't treat as error
              } else {
                activeAudioElements.current.delete(audio);
                reject(error);
              }
            });
        };

        // Load the audio
        audio.load();
      } catch (error) {
        isPlayingRef.current = false;
        if (audioRef.current) {
          activeAudioElements.current.delete(audioRef.current);
        }
        reject(error);
      }
    });
  }, [userHasInteracted, isAnyAudioPlaying, forceStopAllAudio]);

  const stopAudio = useCallback(() => {
    forceStopAllAudio();
  }, [forceStopAllAudio]);

  const isPlaying = useCallback(() => {
    return isAnyAudioPlaying();
  }, [isAnyAudioPlaying]);

  return {
    playAudio,
    isPlaying,
    enableAudio,
    userHasInteracted,
    isAnyAudioPlaying,
    forceStopAllAudio
  };
};

// Audio file definitions for SelectionScreen
export const SELECTION_SCREEN_AUDIO = {
  welcome: { filename: 'selection_welcome_01.mp3', duration: 2000 },
  instructions: { filename: 'selection_instructions_01.mp3', duration: 2500 },
  encouragement: { filename: 'selection_encouragement_01.mp3', duration: 2000 },
  buttonHover: { filename: 'ui_button_hover_01.mp3', duration: 500 }
} as const;

// Audio file definitions for QuestionScreen - Number Naming & Subitizing
export const QUESTION_SCREEN_AUDIO = {
  // Number instruction files (0-20 for K grade)
  tapNumber: {
    0: { filename: 'preKAssessment/numberNaming/question_tap_number_0.mp3', duration: 1000 },
    1: { filename: 'preKAssessment/numberNaming/question_tap_number_1.mp3', duration: 1000 },
    2: { filename: 'preKAssessment/numberNaming/question_tap_number_2.mp3', duration: 1000 },
    3: { filename: 'preKAssessment/numberNaming/question_tap_number_3.mp3', duration: 1000 },
    4: { filename: 'preKAssessment/numberNaming/question_tap_number_4.mp3', duration: 1000 },
    5: { filename: 'preKAssessment/numberNaming/question_tap_number_5.mp3', duration: 1000 },
    6: { filename: 'preKAssessment/numberNaming/question_tap_number_6.mp3', duration: 1000 },
    7: { filename: 'preKAssessment/numberNaming/question_tap_number_7.mp3', duration: 1000 },
    8: { filename: 'preKAssessment/numberNaming/question_tap_number_8.mp3', duration: 1000 },
    9: { filename: 'preKAssessment/numberNaming/question_tap_number_9.mp3', duration: 1000 },
    10: { filename: 'kAssessment/numberNaming/k_tap_number_10.mp3', duration: 1000 },
    11: { filename: 'kAssessment/numberNaming/k_tap_number_11.mp3', duration: 1000 },
    12: { filename: 'kAssessment/numberNaming/k_tap_number_12.mp3', duration: 1000 },
    13: { filename: 'kAssessment/numberNaming/k_tap_number_13.mp3', duration: 1000 },
    14: { filename: 'kAssessment/numberNaming/k_tap_number_14.mp3', duration: 1000 },
    15: { filename: 'kAssessment/numberNaming/k_tap_number_15.mp3', duration: 1000 },
    16: { filename: 'kAssessment/numberNaming/k_tap_number_16.mp3', duration: 1000 },
    17: { filename: 'kAssessment/numberNaming/k_tap_number_17.mp3', duration: 1000 },
    18: { filename: 'kAssessment/numberNaming/k_tap_number_18.mp3', duration: 1000 },
    19: { filename: 'kAssessment/numberNaming/k_tap_number_19.mp3', duration: 1000 },
    20: { filename: 'kAssessment/numberNaming/k_tap_number_20.mp3', duration: 1000 }
  },
  // Subitizing instructions
  subitizing: {
    instruction: { filename: 'subitizing_instruction.mp3', duration: 1500 },
    warmupStart: { filename: 'subitizing_warmup_start.mp3', duration: 3000 },
    warmupReady: { filename: 'subitizing_warmup_ready.mp3', duration: 2000 },
    timeoutPrompt: { filename: 'subitizing_timeout_prompt.mp3', duration: 2000 },
    timeoutEncourage: { filename: 'subitizing_timeout_encourage.mp3', duration: 2500 },
    assessmentIntro: { filename: 'preKAssessment/subitizing/subitizing_intro.mp3', duration: 5000 },
    getReady: { filename: 'preKAssessment/subitizing/subitizing_get_ready.mp3', duration: 1000 },
    // Demo phase - think-aloud modeling before assessment
    demoIntro: { filename: 'assessment/subitizing/demo_intro.mp3', duration: 6000 },
    demoISaw3: { filename: 'assessment/subitizing/demo_i_saw_3.mp3', duration: 4000 },
    demoYourTurn: { filename: 'assessment/subitizing/demo_your_turn.mp3', duration: 2000 }
  },
  // Counting instructions
  counting: {
    instruction: { filename: 'counting_instruction.mp3', duration: 2000 }
  },
  // What comes next instructions
  whatComesNext: {
    instruction: { filename: 'whatcomesnext_instruction.mp3', duration: 1500 }
  },
  // Warmup instructions
  warmup: {
    practiceInstruction: { filename: 'warmup_practice_instruction.mp3', duration: 2000 },
    goodJob: { filename: 'warmup_good_job.mp3', duration: 1500 },
    readyForTest: { filename: 'warmup_ready_for_test.mp3', duration: 2000 },
    transition: { filename: 'preKAssessment/warmup_transition.mp3', duration: 5000 }
  },
  // Positive feedback (variety for engagement)
  feedback: {
    correct1: { filename: 'feedback_correct_1.mp3', duration: 1000 },
    correct2: { filename: 'feedback_correct_2.mp3', duration: 1000 },
    correct3: { filename: 'feedback_correct_3.mp3', duration: 1000 },
    correct4: { filename: 'feedback_correct_4.mp3', duration: 1000 },
    correct5: { filename: 'feedback_correct_5.mp3', duration: 1000 }
  },
  // Timeout recovery prompts
  timeout: {
    gentlePrompt: { filename: 'timeout_gentle_prompt.mp3', duration: 2000 },
    encouragement: { filename: 'timeout_encouragement.mp3', duration: 2000 }
  },
  // Test completion
  completion: { filename: 'test_completion.mp3', duration: 2000 },
  // PreK Practice instructions
  practice: {
    idoIntro: { filename: 'prek_ido_intro.mp3', duration: 2000 },
    idoRepeat: { filename: 'prek_ido_repeat.mp3', duration: 1500 },
    wedoIntro: { filename: 'prek_wedo_intro.mp3', duration: 2000 },
    youdoIntro: { filename: 'prek_youdo_intro.mp3', duration: 1500 },
    practiceComplete: { filename: 'prek_practice_complete.mp3', duration: 2000 }
  },
  // PreK Practice transition messages (pass/fail screens)
  transitions: {
    accuracyPass: { filename: 'preKPractice/numberNaming/transitions/accuracy_pass.mp3', duration: 2000 },
    fluency15Pass: { filename: 'preKPractice/numberNaming/transitions/fluency15_pass.mp3', duration: 2000 },  // Used by K NumberNaming only
    fluency15Fail: { filename: 'preKPractice/numberNaming/transitions/fluency15_fail.mp3', duration: 2000 }   // Used by K NumberNaming only
  },
  // I Do practice - "This is [number]. Say [number]."
  idoNumber: {
    0: { filename: 'preKPractice/numberNaming/iDo/prek_ido_0.mp3', duration: 1500 },
    1: { filename: 'preKPractice/numberNaming/iDo/prek_ido_1.mp3', duration: 1500 },
    2: { filename: 'preKPractice/numberNaming/iDo/prek_ido_2.mp3', duration: 1500 },
    3: { filename: 'preKPractice/numberNaming/iDo/prek_ido_3.mp3', duration: 1500 },
    4: { filename: 'preKPractice/numberNaming/iDo/prek_ido_4.mp3', duration: 1500 },
    5: { filename: 'preKPractice/numberNaming/iDo/prek_ido_5.mp3', duration: 1500 },
    6: { filename: 'preKPractice/numberNaming/iDo/prek_ido_6.mp3', duration: 1500 },
    7: { filename: 'preKPractice/numberNaming/iDo/prek_ido_7.mp3', duration: 1500 },
    8: { filename: 'preKPractice/numberNaming/iDo/prek_ido_8.mp3', duration: 1500 },
    9: { filename: 'preKPractice/numberNaming/iDo/prek_ido_9.mp3', duration: 1500 }
  },
  // K I Do practice - "This is [number]. Say [number]." (digits 0-20)
  kIdoNumber: {
    0: { filename: 'preKPractice/numberNaming/iDo/prek_ido_0.mp3', duration: 1500 },
    1: { filename: 'preKPractice/numberNaming/iDo/prek_ido_1.mp3', duration: 1500 },
    2: { filename: 'preKPractice/numberNaming/iDo/prek_ido_2.mp3', duration: 1500 },
    3: { filename: 'preKPractice/numberNaming/iDo/prek_ido_3.mp3', duration: 1500 },
    4: { filename: 'preKPractice/numberNaming/iDo/prek_ido_4.mp3', duration: 1500 },
    5: { filename: 'preKPractice/numberNaming/iDo/prek_ido_5.mp3', duration: 1500 },
    6: { filename: 'preKPractice/numberNaming/iDo/prek_ido_6.mp3', duration: 1500 },
    7: { filename: 'preKPractice/numberNaming/iDo/prek_ido_7.mp3', duration: 1500 },
    8: { filename: 'preKPractice/numberNaming/iDo/prek_ido_8.mp3', duration: 1500 },
    9: { filename: 'preKPractice/numberNaming/iDo/prek_ido_9.mp3', duration: 1500 },
    10: { filename: 'kPractice/numberNaming/k_ido_10.mp3', duration: 1500 },
    11: { filename: 'kPractice/numberNaming/k_ido_11.mp3', duration: 1500 },
    12: { filename: 'kPractice/numberNaming/k_ido_12.mp3', duration: 1500 },
    13: { filename: 'kPractice/numberNaming/k_ido_13.mp3', duration: 1500 },
    14: { filename: 'kPractice/numberNaming/k_ido_14.mp3', duration: 1500 },
    15: { filename: 'kPractice/numberNaming/k_ido_15.mp3', duration: 1500 },
    16: { filename: 'kPractice/numberNaming/k_ido_16.mp3', duration: 1500 },
    17: { filename: 'kPractice/numberNaming/k_ido_17.mp3', duration: 1500 },
    18: { filename: 'kPractice/numberNaming/k_ido_18.mp3', duration: 1500 },
    19: { filename: 'kPractice/numberNaming/k_ido_19.mp3', duration: 1500 },
    20: { filename: 'kPractice/numberNaming/k_ido_20.mp3', duration: 1500 }
  },
  // Counting I Do practice - "Let's count these dots. One, two, three. I counted 3."
  countingIdo: {
    1: { filename: 'practice/counting/prek_counting_ido_1.mp3', duration: 2500 },
    2: { filename: 'practice/counting/prek_counting_ido_2.mp3', duration: 2500 },
    3: { filename: 'practice/counting/prek_counting_ido_3.mp3', duration: 3000 },
    4: { filename: 'practice/counting/prek_counting_ido_4.mp3', duration: 3500 },
    5: { filename: 'practice/counting/prek_counting_ido_5.mp3', duration: 4000 }
  },
  // Counting We Do practice - "Count with me. One, two, three. Now you tap the answer."
  countingWedo: {
    1: { filename: 'practice/counting/prek_counting_wedo_1.mp3', duration: 2500 },
    2: { filename: 'practice/counting/prek_counting_wedo_2.mp3', duration: 2500 },
    3: { filename: 'practice/counting/prek_counting_wedo_3.mp3', duration: 3000 },
    4: { filename: 'practice/counting/prek_counting_wedo_4.mp3', duration: 3500 },
    5: { filename: 'practice/counting/prek_counting_wedo_5.mp3', duration: 4000 }
  },
  // Individual counting numbers for 1:1 correspondence (dot flashing)
  countingNumbers: {
    1: { filename: 'practice/counting/count_one.mp3', duration: 800 },
    2: { filename: 'practice/counting/count_two.mp3', duration: 800 },
    3: { filename: 'practice/counting/count_three.mp3', duration: 800 },
    4: { filename: 'practice/counting/count_four.mp3', duration: 800 },
    5: { filename: 'practice/counting/count_five.mp3', duration: 800 }
  },
  // Counting intro/outro phrases
  countingPhrases: {
    letsCount: { filename: 'practice/counting/counting_lets_count.mp3', duration: 1500 },
    countWithMe: { filename: 'practice/counting/counting_count_with_me.mp3', duration: 1200 },
    iCounted: { filename: 'practice/counting/counting_i_counted.mp3', duration: 800 },
    nowTap: { filename: 'practice/counting/counting_now_tap.mp3', duration: 1500 }
  },
  // What Comes Next I Do practice - "What comes next? Look at the numbers. One, two... what comes next? Three!"
  whatComesNextIdo: {
    3: { filename: 'practice/whatcomesnext/prek_whatcomesnext_ido_3.mp3', duration: 5000 },
    4: { filename: 'practice/whatcomesnext/prek_whatcomesnext_ido_4.mp3', duration: 5000 },
    5: { filename: 'practice/whatcomesnext/prek_whatcomesnext_ido_5.mp3', duration: 5000 },
    6: { filename: 'practice/whatcomesnext/prek_whatcomesnext_ido_6.mp3', duration: 5000 },
    7: { filename: 'practice/whatcomesnext/prek_whatcomesnext_ido_7.mp3', duration: 5000 },
    8: { filename: 'practice/whatcomesnext/prek_whatcomesnext_ido_8.mp3', duration: 5000 },
    9: { filename: 'practice/whatcomesnext/prek_whatcomesnext_ido_9.mp3', duration: 5000 }
  },
  // What Comes Next We Do practice - "What comes next? One, two... Now you tap the answer!"
  whatComesNextWedo: {
    3: { filename: 'practice/whatcomesnext/prek_whatcomesnext_wedo_3.mp3', duration: 3500 },
    4: { filename: 'practice/whatcomesnext/prek_whatcomesnext_wedo_4.mp3', duration: 3500 },
    5: { filename: 'practice/whatcomesnext/prek_whatcomesnext_wedo_5.mp3', duration: 3500 },
    6: { filename: 'practice/whatcomesnext/prek_whatcomesnext_wedo_6.mp3', duration: 3500 },
    7: { filename: 'practice/whatcomesnext/prek_whatcomesnext_wedo_7.mp3', duration: 3500 },
    8: { filename: 'practice/whatcomesnext/prek_whatcomesnext_wedo_8.mp3', duration: 3500 },
    9: { filename: 'practice/whatcomesnext/prek_whatcomesnext_wedo_9.mp3', duration: 3500 }
  }
} as const;

// Programmatic sound generation for immediate feedback
const createProgrammaticSound = (type: 'chime' | 'boop'): Promise<void> => {
  return new Promise((resolve) => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const gainNode = audioContext.createGain();
      gainNode.connect(audioContext.destination);

      if (type === 'chime') {
        // Create pleasant ascending chime (C-E-G chord)
        const frequencies = [261.63, 329.63, 392.00]; // C4, E4, G4
        const duration = 0.4; // 400ms

        frequencies.forEach((freq, index) => {
          const oscillator = audioContext.createOscillator();
          const noteGain = audioContext.createGain();

          oscillator.connect(noteGain);
          noteGain.connect(gainNode);

          oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
          oscillator.type = 'sine';

          // Gentle attack and decay
          noteGain.gain.setValueAtTime(0, audioContext.currentTime);
          noteGain.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05);
          noteGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

          oscillator.start(audioContext.currentTime + index * 0.1);
          oscillator.stop(audioContext.currentTime + duration);
        });

        setTimeout(resolve, duration * 1000);
      } else {
        // Create short boop sound (lower frequency buzz)
        const oscillator = audioContext.createOscillator();
        oscillator.connect(gainNode);

        oscillator.frequency.setValueAtTime(150, audioContext.currentTime); // Low frequency
        oscillator.type = 'square'; // Harsher sound for error

        // Quick attack and decay
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.25);

        setTimeout(resolve, 250);
      }
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
      resolve(); // Don't block if audio fails
    }
  });
};

// Specialized hook for TTS with timer coordination
export const usePreKTTS = () => {
  const { playAudio, isPlaying, isAnyAudioPlaying, forceStopAllAudio } = usePreKAudio();
  const [isTTSPlaying, setIsTTSPlaying] = useState(false);
  const [ttsCompletedRef, setTTSCompletedRef] = useState(false);

  // Play TTS and return a promise that resolves when audio completes
  const playTTSWithCallback = useCallback(async (audioFile: AudioFile, onComplete?: () => void): Promise<void> => {
    setIsTTSPlaying(true);
    setTTSCompletedRef(false);

    try {
      await playAudio(audioFile);
      setTTSCompletedRef(true);
      setIsTTSPlaying(false);

      // Call completion callback after TTS finishes
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      setTTSCompletedRef(true);
      setIsTTSPlaying(false);

      // Still call completion callback even if audio fails
      if (onComplete) {
        onComplete();
      }
    }
  }, [playAudio]);

  // Play random feedback audio from available options
  const playRandomFeedback = useCallback(async (): Promise<void> => {
    // Extra safety check before playing feedback
    if (isAnyAudioPlaying()) {
      return Promise.resolve();
    }

    const feedbackOptions = [
      QUESTION_SCREEN_AUDIO.feedback.correct1,
      QUESTION_SCREEN_AUDIO.feedback.correct2,
      QUESTION_SCREEN_AUDIO.feedback.correct3,
      QUESTION_SCREEN_AUDIO.feedback.correct4,
      QUESTION_SCREEN_AUDIO.feedback.correct5
    ];

    const randomFeedback = feedbackOptions[Math.floor(Math.random() * feedbackOptions.length)];
    return await playAudio(randomFeedback);
  }, [playAudio, isAnyAudioPlaying]);

  // Stop any current TTS - AGGRESSIVE CLEANUP
  const stopTTS = useCallback(() => {
    forceStopAllAudio();
    setIsTTSPlaying(false);
    setTTSCompletedRef(true);
  }, [forceStopAllAudio]);

  // Play immediate feedback sounds (always play regardless of speechEnabled)
  const playChime = useCallback(async (): Promise<void> => {
    return createProgrammaticSound('chime');
  }, []);

  const playBoop = useCallback(async (): Promise<void> => {
    return createProgrammaticSound('boop');
  }, []);

  return {
    playTTSWithCallback,
    playRandomFeedback,
    stopTTS,
    isTTSPlaying,
    ttsCompleted: ttsCompletedRef,
    playChime,
    playBoop,
    // Expose original audio functions
    playAudio,
    isPlaying
  };
};